import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Competitor_research_menu } from './Competitor/Competitor_menu';
import OverviewFilters from './LocationOverview/OverviewFilters';
import OverviewTopSection from './LocationOverview/OverviewTopSection';
import { OverviewMarketSegmentation } from './LocationOverview/OverviewMarketSegmentation';
import { OverviewVisitorsDemographic } from './LocationOverview/OverviewVisitorsDemographic';
import { OverviewTopPerfomers } from './LocationOverview/OverviewTopPerformers';
import  OverviewSidebar from './LocationOverview/OverviewSidebar';
import OverviewPopup from './LocationOverview/OverviewPopup'
import dayjs from 'dayjs';
import mapboxgl from 'mapbox-gl';

const LocationOverview = () => {
    const map = useRef(null);
    const mapContainer = useRef(null);
    const markersRef = useRef([]);
    const heatmapLayerId = 'heatmap-layer';
    const heatmapSourceId = 'heatmap-data';
    
    const period_comparison_options = [
        { label: 'Month over Month (MoM)', value: 'mom' },
        { label: 'Quarter over Quarter (QoQ)', value: 'qoq' },
        { label: 'Year over Year (YoY)', value: 'yoy' }
    ];
    
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [businessLocations, setBusinessLocations] = useState();
    const [sidebarValue, setSidebarValue] = useState();

    const [selectedPeriodComparison, setSelectedPeriodComparison] = useState(
        period_comparison_options[0]
    );
    const [dateRange, setDateRange] = useState({
        startDate: dayjs().subtract(30, 'days').toDate(),
        endDate: dayjs().toDate(),
        key: 'selection',
    });
    const [locationOptions, setLocationOptions] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [businessRevenue, setBusinessRevenue] = useState([]);

    // Clear existing markers
    const clearMarkers = () => {
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
    };

    // Clear existing heatmap
    const clearHeatmap = () => {
        if (!map.current) return;
        
        // Remove all layers that use the heatmap source first
        if (map.current.getLayer('heatmap-points')) {
            map.current.removeLayer('heatmap-points');
        }
        if (map.current.getLayer(heatmapLayerId)) {
            map.current.removeLayer(heatmapLayerId);
        }
        
        // Then remove the source
        if (map.current.getSource(heatmapSourceId)) {
            map.current.removeSource(heatmapSourceId);
        }
    };

    // Create markers for business locations
    const createMarkers = () => {
        if (!map.current || !businessLocations || businessLocations.length === 0) return;
        
        clearMarkers();
        
        businessLocations.forEach((location, index) => {
            if(!selectedLocations.includes(location.name)){
                return
            }
            // Create marker container element
            const markerElement = document.createElement('div');
            markerElement.className = 'business-marker';
            markerElement.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    cursor: pointer;
                ">
                    <div style="
                        font-size: 14px;
                        color: #333;
                        white-space: nowrap;
                        margin-top: 5px;
                    ">
                        ${location.name}
                    </div>
                </div>
            `;
    
            // Create popup content
            const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <div style="padding: 10px;">
                    <h3 style="margin: 0 0 5px 0; color: #333;">${location.name}</h3>
                    <p style="margin: 0; color: #666; font-size: 12px;">${location.address}</p>
                    <p style="margin: 5px 0 0 0; color: #666; font-size: 12px;">
                        Address: ${location.address}
                    </p>
                </div>
            `);
    
            // Create and add marker (using default marker)
            const marker = new mapboxgl.Marker()
                .setLngLat(location.coordinates)
                .setPopup(popup)
                .addTo(map.current);
    
            // Add custom label element positioned below the marker
            const labelElement = document.createElement('div');
            labelElement.className = 'marker-label';
            labelElement.innerHTML = `
                <div style="
                    position: absolute;
                    top: 35px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 14px;
                    color: #333;
                    white-space: nowrap;
                    pointer-events: none;
                    text-align: center;
                ">
                    ${location.name}
                </div>
            `;
            
            // Add label to marker element
            marker.getElement().appendChild(labelElement);
    
            markersRef.current.push(marker);
        });
    };

    // Create heatmap from analytics data
    const createHeatmap = () => {
        console.log('createHeatmap: ', !map.current, analyticsData, businessLocations)
        if (!map.current || !analyticsData || !businessLocations) return;

        clearHeatmap();

        const heatmapData = analyticsData.current_period?.heatmap;
        if (!heatmapData || heatmapData.length === 0) return;

        // Create GeoJSON features for heatmap
        const features = [];
        
        businessLocations.forEach((location) => {
            console.log('createHeatmap: ', selectedLocations.includes(location.name))
            if (location.heatmap && location.heatmap.length > 0 && selectedLocations.includes(location.name)) {
                location.heatmap.forEach((coord, index) => {
                    if (index < heatmapData.length) {
                        const weight = heatmapData[index];
                        if (weight > 0) {
                            features.push({
                                type: 'Feature',
                                properties: {
                                    weight: weight,
                                    location: location.name,
                                    index: index
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: coord
                                }
                            });
                        }
                    }
                });
            }
        });

        if (features.length === 0) return;

        // Add heatmap source
        map.current.addSource(heatmapSourceId, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: features
            }
        });

        // Add heatmap layer
        map.current.addLayer({
            id: heatmapLayerId,
            type: 'heatmap',
            source: heatmapSourceId,
            maxzoom: 15,
            paint: {
                // Increase the heatmap weight based on frequency and property magnitude
                'heatmap-weight': [
                    'interpolate',
                    ['linear'],
                    ['get', 'weight'],
                    0, 0,
                    200, 0.2,
                    300, 1
                ],
                // Increase the heatmap color weight by zoom level
                // heatmap-intensity is a multiplier on top of heatmap-weight
                'heatmap-intensity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0, 1,
                    15, 3
                ],
                // Color ramp for heatmap
                'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0, 'rgba(33,102,172,0)',
                    0.2, 'rgb(103,169,207)',
                    0.4, 'rgb(209,229,240)',
                    0.6, 'rgb(253,219,199)',
                    0.8, 'rgb(239,138,98)',
                    1, 'rgb(178,24,43)'
                ],
                // Adjust the heatmap radius by zoom level
                'heatmap-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0, 2,
                    15, 20
                ],
                // Transition from heatmap to circle layer by zoom level
                'heatmap-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    7, 1,
                    15, 0
                ]
            }
        });

        // Add circle layer for individual points at high zoom levels
        map.current.addLayer({
            id: 'heatmap-points',
            type: 'circle',
            source: heatmapSourceId,
            minzoom: 14,
            paint: {
                'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', 'weight'],
                    200, 5,
                    300, 15
                ],
                'circle-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'weight'],
                    200, 'rgb(103,169,207)',
                    250, 'rgb(253,219,199)',
                    300, 'rgb(178,24,43)'
                ],
                'circle-stroke-color': 'white',
                'circle-stroke-width': 1,
                'circle-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    15, 1
                ]
            }
        });

        // Add click event for heatmap points
        map.current.on('click', 'heatmap-points', (e) => {
            const coordinates = e.features[0].geometry.coordinates.slice();
            const properties = e.features[0].properties;
            
            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(`
                    <div style="padding: 8px;">
                        <h4 style="margin: 0 0 5px 0;">Heatmap Point</h4>
                        <p style="margin: 0; font-size: 12px;">Location: ${properties.location}</p>
                        <p style="margin: 0; font-size: 12px;">Weight: ${properties.weight}</p>
                        <p style="margin: 0; font-size: 12px;">Index: ${properties.index}</p>
                    </div>
                `)
                .addTo(map.current);
        });

        // Change cursor on hover
        map.current.on('mouseenter', 'heatmap-points', () => {
            map.current.getCanvas().style.cursor = 'pointer';
        });

        map.current.on('mouseleave', 'heatmap-points', () => {
            map.current.getCanvas().style.cursor = '';
        });
    };

    // Update map when data changes
    const updateMap = (source) => {
        if (!map.current) return;

        // Wait for map to be fully loaded
        // if (!map.current.isStyleLoaded()) {
        //     map.current.on('styledata', updateMap);
        //     return;
        // }
        console.log('updateMap: ', source)
        createMarkers();
        createHeatmap();

        // Fit map to show all markers if we have business locations
        if (businessLocations && businessLocations.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            businessLocations.forEach(location => {
                bounds.extend(location.coordinates);
            });
            
            // Add some padding around the bounds
            map.current.fitBounds(bounds, {
                padding: 50,
                maxZoom: 12
            });
        }
    }; 
    const arraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((val, index) => val === arr2[index]);
    }

    // Fetch analytics data from API
    const fetchAnalyticsData = async ({startDate, endDate, selectedPeriodComparison, selectedLocations}) => {
        setLoading(true);
        setError(null);
        const urlParams = new URLSearchParams(window.location.search);

        try {
            const requestBody = {
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0],
                comparison_period: selectedPeriodComparison,
                location: selectedLocations,
                demoToken: urlParams.get('demo-token')

            };
            const response = await axios.post('/overviewapi/analytics', requestBody);
            const data = response.data;
            setAnalyticsData(data);

            console.log('fetchAnalyticsData: ', !arraysEqual(data.period.locations, selectedLocations), selectedLocations)
            if(!arraysEqual(data.period.locations, selectedLocations)){
                
                setSelectedLocations(data.period.locations);
            }

            if(data.current_period.revenueByLocation){
                var busLocations = businessLocations;
                if(data.period.business_locations){
                    busLocations = data.period.business_locations;
                }
                // console.log('busLocations: ', busLocations)
                const business_revenue = busLocations.map((bus) => {
                    const revenue = data.current_period.revenueByLocation[bus['name']]
                    return {...bus, revenue}
                })
                // console.log('business_revenue: ', business_revenue)
                setBusinessRevenue(business_revenue)
            }else{
                setBusinessRevenue([])
            }

            
            if(data.period.business_locations){
                setBusinessLocations(data.period.business_locations);
                setLocationOptions(data.period.locations);
            }
            
            console.log('Analytics data received:', data);
        } catch (err) {
            console.error('Error fetching analytics data:', err);
            setError(err.response?.data?.error || 'Failed to fetch analytics data');
        } finally {
            setLoading(false);
        }
    };

    // Handle filter changes from OverviewFilters component
    const onFilterConditionChange = (selectedLocations, selectedPeriodComparison, startDate, endDate) => {
        if (startDate && endDate) {
            fetchAnalyticsData({
                selectedLocations,
                selectedPeriodComparison: selectedPeriodComparison.value,
                startDate,
                endDate
            });
        
            setDateRange({    
                startDate: startDate,
                endDate: endDate,
                key: 'selection',
            });
            setSelectedLocations(selectedLocations);
            setSelectedPeriodComparison(selectedPeriodComparison);
        }
    };

    

    // Helper function to format numbers with commas
    const numberWithCommas = (x) => {
        if (!x) return "No Data";
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // Initialize map
    useEffect(() => {
        if (map.current) return; 
        
        mapboxgl.accessToken = 'pk.eyJ1Ijoic25pa2FlaW4iLCJhIjoiY2s1eWNpcWxrMDJvbzNtcDdyb3puNTVrMCJ9.t-VFWsbpRo--tRSLgLa9ng';
        
        map.current = new mapboxgl.Map({
            container: "mapbox",
            style: 'mapbox://styles/mapbox/light-v10',
            center: [-95, 38.2],
            zoom: 3.5
        });
        
        map.current.scrollZoom.disable();
        map.current.addControl(new mapboxgl.NavigationControl());

        // Update map when style is loaded
        map.current.on('load', () => {
            console.log('Map loaded, updating markers and heatmap');
            updateMap('map loaded');
        });
    }, []);

    // Update map when analytics data or business locations change
    useEffect(() => {
        console.log('Analytics data changed:', analyticsData);
        console.log('Business locations:', businessLocations);
        console.log('Selected locations:', selectedLocations);
        console.log('Map loaded:', map.current, map.current.isStyleLoaded());

        if (map.current) {
            updateMap('analyticsData');
            
        //     if (map.current.isStyleLoaded()) {
        //         updateMap('analyticsData');
        //     } else {
        //         // Wait for style to load, then update
        //         map.current.once('styledata', () => {
        //             console.log('Style loaded, now updating map');
        //             updateMap('analyticsData');
        //         });
        //     }
        }
    }, [analyticsData, businessLocations, selectedLocations]);

    // useEffect(() => {
    //     if (map.current && map.current.isStyleLoaded()) {
    //         updateMap('selectedLocations');
    //     }
    // }, [selectedLocations])

    // Transform analytics data for child components
    const getTransformedData = () => {
        if (!analyticsData) return null;
        const current = analyticsData.current_period;
        const comparison = analyticsData.comparison_period;
        return {
            current: current,
            comparison: comparison,
            changes: analyticsData.changes || {},
            period: analyticsData.period,
            comparisonInfo: analyticsData.comparison_info
        };
    };

    // Check authentication on mount
    useEffect(() => {
        const token = localStorage.getItem("auth-token");
        if (!token) {
            console.log('No auth token found');
        }
    }, []);

    // Initial data fetch
    // useEffect(() => {
    //     fetchAnalyticsData({
    //         selectedLocations,
    //         selectedPeriodComparison: selectedPeriodComparison.value,
    //         startDate: dateRange.startDate,
    //         endDate: dateRange.endDate
    //     });
    // }, [dateRange]);

    // Placeholder functions for components that need them
    const login = () => {
        console.log('Login clicked');
    };

    const register = () => {
        console.log('Register clicked');
    };

    const focusLocation = (location) => {
        const coordinates = location['coordinates']
        // console.log('location: ', location)
        map.current.flyTo({
            center: coordinates,
            zoom: 10
            });
    };

    const transformedData = getTransformedData();

    const boxClickFunction = ({key, value}) => {
        setSidebarValue(key)
    }

    const closeSidebar = () => {
        setSidebarValue(null)
    }

    return (
        <div>
            <OverviewPopup/>
            <OverviewSidebar sidebarValue={sidebarValue} closeSidebar={closeSidebar}/>
            <Competitor_research_menu 
                freetier={false} 
                register={register} 
                login={login} 
                section_hide={false}
            />
            
            <div className="p-15">

                <OverviewFilters 
                    dateRange={dateRange}
                    selectedPeriodComparison={selectedPeriodComparison}
                    period_comparison_options={period_comparison_options}
                    locationOptions={locationOptions}
                    selectedLocations={selectedLocations}
                    onFilterConditionChange={onFilterConditionChange}
                    loading={loading}
                />
                
                {error && (
                    <div className="error-message" style={{ 
                        color: 'red', 
                        padding: '10px', 
                        margin: '10px 0',
                        border: '1px solid red',
                        borderRadius: '4px',
                        backgroundColor: '#ffe6e6'
                    }}>
                        Error: {error}
                    </div>
                )}
                
                {loading && (
                    <div className="loading-message" style={{
                        padding: '20px',
                        textAlign: 'center',
                        fontSize: '16px'
                    }}>
                        Loading analytics data...
                    </div>
                )}
                
                <OverviewTopSection 
                    analyticsData={transformedData}
                    loading={loading}
                    numberWithCommas={numberWithCommas}
                    boxClickFunction={boxClickFunction}
                />

                
                <OverviewTopPerfomers 
                    competitor={[]}
                    focusLocation={focusLocation}
                    freetier={false}
                    businessRevenue={businessRevenue}
                    loading={loading}
                />
                
                <OverviewVisitorsDemographic 
                    analyticsData={transformedData}
                    loading={loading}
                />
                <OverviewMarketSegmentation
                    analyticsData={transformedData}
                />
            </div>
        </div>
    );
};

export default LocationOverview;